import React from 'react';
import Table from 'react-bootstrap/Table';

const SpreadsAllTable = ({
  spreadsData,
  bookLogos,
  getBookLogoUrl,
  getTeamLogoUrl,
  getCellStyle,
  getTooltipText,
  onOddsClick,
  game,
  oddsCellStyle,
  periodsToShow = null  // If null, show all periods. Otherwise, array of periods to display
}) => {
  const safeGame = { away: 'TBD', home: 'TBD', gameIDmlb: 'loading', ...game };

  if (!spreadsData || spreadsData.length === 0) {
    return null;
  }

  // Group data by period
  const groupedByPeriod = spreadsData.reduce((acc, player) => {
    const period = player.period || 'Game';
    if (!acc[period]) {
      acc[period] = [];
    }
    acc[period].push(player);
    return acc;
  }, {});

  // Define period order
  const periodOrder = ['Game', 'Q1', 'Q2', 'Q3', 'Q4', '1st Half', '2nd Half', '1st', '2nd', '3rd'];

  // Filter periods based on periodsToShow prop
  let periodsToDisplay = Object.keys(groupedByPeriod);
  if (periodsToShow && Array.isArray(periodsToShow)) {
    periodsToDisplay = periodsToDisplay.filter(period => periodsToShow.includes(period));
  }

  const sortedPeriods = periodsToDisplay.sort((a, b) => {
    const indexA = periodOrder.indexOf(a);
    const indexB = periodOrder.indexOf(b);
    return (indexA === -1 ? 999 : indexA) - (indexB === -1 ? 999 : indexB);
  });

  return (
    <div className="table-container">
      <Table bordered size="sm" className="text-center">
        <thead>
          <tr><th colSpan={bookLogos.length + 1} className="bg-light">Spreads (All Periods)</th></tr>
          <tr>
            <th style={{ width: '120px' }}></th>
            {bookLogos.map(book => (
              <th key={`spreads-${book}`} style={oddsCellStyle}>
                <img
                  src={getBookLogoUrl(book)}
                  alt={`${book} logo`}
                  title={book}
                  style={{ height: '20px' }}
                />
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {sortedPeriods.map((period) => (
            <React.Fragment key={period}>
              {/* Period section header */}
              <tr>
                <td colSpan={bookLogos.length + 1} style={{
                  backgroundColor: 'transparent',
                  color: '#ffc107',
                  fontWeight: 'bold',
                  textAlign: 'center',
                  padding: '8px',
                  borderTop: 'none',
                  borderBottom: 'none'
                }}>
                  {period === 'Game' ? 'Spreads' : `Spreads ${period}`}
                </td>
              </tr>

              {/* Away Team Row */}
              <tr>
                <td style={{ width: '120px', fontWeight: 'bold' }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px' }}>
                    <img
                      src={safeGame.awayLogo || getTeamLogoUrl(safeGame.away)}
                      alt={safeGame.away}
                      style={{ width: '16px', height: '16px' }}
                      onError={(e) => {
                        e.target.style.visibility = 'hidden';
                        e.target.style.width = '0px';
                        e.target.style.height = '0px';
                        e.target.style.margin = '0px';
                      }}
                    />
                    <span>{safeGame.away}</span>
                  </div>
                </td>
                {bookLogos.map(book => {
                  // Find away spread data for this period and book
                  const awayData = groupedByPeriod[period].find(d =>
                    d.team === 'away' &&
                    d[`spreads_${book}`] !== null &&
                    d[`spreads_${book}`] !== undefined
                  );

                  return (
                    <td
                      key={`spreads-${book}-away`}
                      className="odds-cell"
                      style={{
                        ...getCellStyle(
                          awayData?.[`spreads_${book}`],
                          awayData?.[`spreads_bestBookPrice`],
                          awayData?.[`spreads_bestBookDiff`],
                          bookLogos.filter(b => awayData?.[`spreads_${b}`] !== null && awayData?.[`spreads_${b}`] !== undefined).length,
                          awayData?.[`spreads_${book}_direction`]
                        ),
                        textAlign: 'center',
                        padding: '8px 4px',
                        fontSize: '0.8rem',
                        cursor: awayData ? 'pointer' : 'default'
                      }}
                      title={getTooltipText(
                        awayData?.[`spreads_${book}`],
                        awayData?.[`spreads_bestBookPrice`],
                        awayData?.[`spreads_bestBookDiff`],
                        bookLogos.filter(b => awayData?.[`spreads_${b}`] !== null && awayData?.[`spreads_${b}`] !== undefined).length
                      )}
                      onClick={() => {
                        if (awayData && onOddsClick) {
                          onOddsClick(safeGame.gameIDmlb, null, 'away', 'spreads', book, awayData[`spreads_${book}`], safeGame.away, safeGame.home);
                        }
                      }}
                    >
                      {awayData ? (
                        <div>
                          <div style={{ fontWeight: 'bold' }}>{awayData[`spreads_${book}`] > 0 ? `+${awayData[`spreads_${book}`]}` : awayData[`spreads_${book}`]}</div>
                          <div style={{ fontSize: '0.75rem', color: '#6c757d' }}>{awayData.point > 0 ? `+${awayData.point}` : awayData.point}</div>
                        </div>
                      ) : null}
                    </td>
                  );
                })}
              </tr>

              {/* Home Team Row */}
              <tr>
                <td style={{ width: '120px', fontWeight: 'bold' }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px' }}>
                    <img
                      src={safeGame.homeLogo || getTeamLogoUrl(safeGame.home)}
                      alt={safeGame.home}
                      style={{ width: '16px', height: '16px' }}
                      onError={(e) => {
                        e.target.style.visibility = 'hidden';
                        e.target.style.width = '0px';
                        e.target.style.height = '0px';
                        e.target.style.margin = '0px';
                      }}
                    />
                    <span>{safeGame.home}</span>
                  </div>
                </td>
                {bookLogos.map(book => {
                  // Find home spread data for this period and book
                  const homeData = groupedByPeriod[period].find(d =>
                    d.team === 'home' &&
                    d[`spreads_${book}`] !== null &&
                    d[`spreads_${book}`] !== undefined
                  );

                  return (
                    <td
                      key={`spreads-${book}-home`}
                      className="odds-cell"
                      style={{
                        ...getCellStyle(
                          homeData?.[`spreads_${book}`],
                          homeData?.[`spreads_bestBookPrice`],
                          homeData?.[`spreads_bestBookDiff`],
                          bookLogos.filter(b => homeData?.[`spreads_${b}`] !== null && homeData?.[`spreads_${b}`] !== undefined).length,
                          homeData?.[`spreads_${book}_direction`]
                        ),
                        textAlign: 'center',
                        padding: '8px 4px',
                        fontSize: '0.8rem',
                        cursor: homeData ? 'pointer' : 'default'
                      }}
                      title={getTooltipText(
                        homeData?.[`spreads_${book}`],
                        homeData?.[`spreads_bestBookPrice`],
                        homeData?.[`spreads_bestBookDiff`],
                        bookLogos.filter(b => homeData?.[`spreads_${b}`] !== null && homeData?.[`spreads_${b}`] !== undefined).length
                      )}
                      onClick={() => {
                        if (homeData && onOddsClick) {
                          onOddsClick(safeGame.gameIDmlb, null, 'home', 'spreads', book, homeData[`spreads_${book}`], safeGame.away, safeGame.home);
                        }
                      }}
                    >
                      {homeData ? (
                        <div>
                          <div style={{ fontWeight: 'bold' }}>{homeData[`spreads_${book}`] > 0 ? `+${homeData[`spreads_${book}`]}` : homeData[`spreads_${book}`]}</div>
                          <div style={{ fontSize: '0.75rem', color: '#6c757d' }}>{homeData.point > 0 ? `+${homeData.point}` : homeData.point}</div>
                        </div>
                      ) : null}
                    </td>
                  );
                })}
              </tr>
            </React.Fragment>
          ))}
        </tbody>
      </Table>
    </div>
  );
};

export default SpreadsAllTable;
